import { File02Icon, LinkSquare02Icon, ArrowLeft02Icon, ArrowRight02Icon, Download02Icon } from "hugeicons-react";
import GoogleDriveIconSvg from "../../../../assets/svg/Google_Drive_icon";
import { CloseButton } from "../../Buttons/CloseButton";
import { useAttachmentContext } from "../../../../contexts/AttachmentContext";
import { useAuth } from "../../../../contexts/AuthContext";
import '../../../../Styles/Layout/Input-Area/googleDriveModal.css';
import { HoverToolTip } from "../../Items/HoverToolTip";
import { ACCEPTED_FILE_TYPES } from "../../../../config/AcceptedFileTypes";
import { useState, useRef, useEffect, useCallback } from "react";
import { GoogleDriveFile } from "../../../../types/files";
import { retrieveGoogleDriveFile } from "../../../../services/api";

interface GoogleDriveFileItemProps {
  file: GoogleDriveFile;
  isSelected: boolean;
  onSelect: (file: GoogleDriveFile, isSelected: boolean) => void;
  onFolderClick: (file: GoogleDriveFile) => void;
}

const GoogleDriveFileItem = ({ file, isSelected, onSelect, onFolderClick }: GoogleDriveFileItemProps) => {
  const isFolder = file.mimeType === 'application/vnd.google-apps.folder';

  const handleSelect = () => {
    if (isFolder) {
      onFolderClick(file);
      return;
    }
    if (!isAccetableType(file)) {
      return;
    }
    onSelect(file, !isSelected);
  }

  const isAccetableType = (file: GoogleDriveFile) => {
    return Object.entries(ACCEPTED_FILE_TYPES).some(([acceptedMimePattern, extensions]) => {
      if (acceptedMimePattern.endsWith('/*')) {
        const [baseType] = acceptedMimePattern.split('/');
        if (file.mimeType.startsWith(baseType)) {
          return true;
        }
      } else if (file.mimeType === acceptedMimePattern) {
        return true;
      }
      return false;
    });
  }

  const handlePreviewinNewTab = async (file: GoogleDriveFile) => {
    window.open(file.webViewLink, '_blank');
  }

  const handleDownload = async (fileToDownload: GoogleDriveFile) => {
    try {
      const blob = await retrieveGoogleDriveFile(fileToDownload.id);

      // Create a blob URL
      const url = URL.createObjectURL(blob);

      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      a.download = fileToDownload.name; // Set the file name for download

      // Append the anchor to the body and click it
      document.body.appendChild(a);
      a.click();

      // Clean up: remove the anchor and revoke the blob URL
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading Google Drive file:', error);
      // Optionally, show an error message to the user
      alert('Failed to download file.'); // Simple alert for demonstration
    }
  };

  const formatBytes = (bytes: number | undefined) => {
    if (!bytes) return '';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const [imageLoadError, setImageLoadError] = useState(false);

  return (
    <div
      className={`google-drive-file-item ${isFolder ? 'folder' : ''} ${isSelected ? 'selected' : ''} ${!isAccetableType(file) && !isFolder ? 'unsupported' : ''}`}
      onClick={handleSelect}
    >
      <div className="google-drive-file-item-icon">
        {file.iconLink && !imageLoadError ? (
          <img
            src={file.iconLink}
            alt="File Icon"
            onError={() => setImageLoadError(true)}
          />
        ) : (
          <File02Icon size={20} />
        )}
      </div>
      <h2 className="google-drive-file-item-name">{file.name}</h2>
      <div className="google-drive-file-item-actions">
        <span className="google-drive-file-item-size">{formatBytes(Number(file.size))}</span>
        {!isFolder && (
          <button
            type="button"
            className="space-chat-button"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(file);
            }}
          >
            <Download02Icon size={16} />
            <HoverToolTip text="Download File" position="top" />
          </button>
        )}

        <button
          type="button"
          className="space-chat-button"
          onClick={(e) => {
            e.stopPropagation();
            handlePreviewinNewTab(file);
          }}
        >
          <LinkSquare02Icon size={16} />
          <HoverToolTip text="Preview in New Tab" position="left" />
        </button>
      </div>
    </div>
  );
};

const GoogleDriveFileItemSkeleton = () => {
  return (
    <div className={`google-drive-file-item-skeleton`}>
      <div className="google-drive-file-item-icon-skeleton">
      </div>
      <h2 className="google-drive-file-item-name-skeleton"> </h2>
      <div className="google-drive-file-item-actions-skeleton">
        <span className="google-drive-file-item-size-skeleton"> </span>
        <div className="google-drive-file-item-action-button-skeleton"> </div>
      </div>
    </div>
  );
};

const GoogleDriveFileSkeleton = () => {
  return (
    <>
      <input
        type="text"
        placeholder="Search files"
        disabled
        className="google-drive-search-input"
      />
      {[...Array(10)].map((_, index) => (
        <GoogleDriveFileItemSkeleton key={index} />
      ))}
    </>
  );
};

export const GoogleDriveModal = () => {
  const {
    googleDriveFiles,
    isGoogleDriveModalOpen,
    handleGoogleDriveModalClose,
    handleGoogleDriveFileSelect,
    isGoogleDriveFileSelected,
    processGoogleDriveFiles,
    isGoogleDriveFilesLoading,
    isGoogleDriveFileUploading,
    driveFolderStack,
    handleGoogleDriveFolderClick,
    handleGoogleDriveNavigateUp,
    handleGoogleDriveNavigateRoot,
    googleDriveError
  } = useAttachmentContext();
  const { user, connectGoogleDrive } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset search and pagination when reopening the modal
  useEffect(() => {
    if (isGoogleDriveModalOpen) {
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [isGoogleDriveModalOpen]);

  const filteredFiles = googleDriveFiles.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredFiles.length / ITEMS_PER_PAGE));
  const paginatedFiles = filteredFiles.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const handlePrevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      handleGoogleDriveModalClose();
    }
  }, [handleGoogleDriveModalClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);


  if (!user || !isGoogleDriveModalOpen) {
    return null;
  }

  return (
    <div className="google-drive-modal-overlay">
      <div className="google-drive-modal" ref={modalRef}>
        <div className="google-drive-modal-header">
          <div className="google-drive-modal-header-left">
            {driveFolderStack.length > 1 && (
              <button type="button" onClick={handleGoogleDriveNavigateUp} className="google-drive-back-button">
                <ArrowLeft02Icon size={20} />
              </button>
            )}
            <GoogleDriveIconSvg size={30} />
            <div className="google-drive-modal-header-path">
              {driveFolderStack.length > 0 && (
                <>
                  <span className="google-drive-path-first" onClick={handleGoogleDriveNavigateRoot} style={{ cursor: 'pointer' }}>
                    {driveFolderStack[0].name}
                  </span>
                  {driveFolderStack.length > 1 && (
                    <>
                      <span className="google-drive-path-separator"> / </span>
                      <span className="google-drive-path-latter">
                        {driveFolderStack.slice(1).map(entry => entry.name).join(' / ')}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <CloseButton onClick={handleGoogleDriveModalClose} />
        </div>
        <div className="google-drive-modal-body">
          {isGoogleDriveFilesLoading ? (
            <GoogleDriveFileSkeleton />
          ) : googleDriveError ? (
            <div className="google-drive-empty-view">
              <GoogleDriveIconSvg size={80} />
              <h2>Error Accessing Google Drive</h2>
              <p>Your Google Drive connection might have expired or there was an issue loading your files. Please try re-linking your account.</p>
              <button
                className="google-drive-relink-button"
                onClick={() => {
                  connectGoogleDrive();
                  handleGoogleDriveModalClose(); // Close modal while redirecting
                }}
              >
                Re-link Account
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search files"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="google-drive-search-input"
              />
              {paginatedFiles.length > 0 ? (
                paginatedFiles.map((file: GoogleDriveFile) => (
                  <GoogleDriveFileItem
                    key={file.id}
                    file={file}
                    isSelected={isGoogleDriveFileSelected(file)}
                    onSelect={handleGoogleDriveFileSelect}
                    onFolderClick={handleGoogleDriveFolderClick}
                  />
                ))
              ) : (
                <div className="google-drive-empty-view">
                  <GoogleDriveIconSvg size={80} />
                  {searchQuery ? (
                    <h2>No files found for "{searchQuery}"</h2>
                  ) : (
                    <h2>No files available in your Google Drive</h2>
                  )}
                  {!searchQuery && (
                    <p>Try uploading files to your Google Drive to see them here.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="google-drive-modal-footer">
          <div className="google-drive-pagination">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="google-drive-pagination-button"
            >
              <ArrowLeft02Icon size={16} />
            </button>
            <span className="google-drive-pagination-info">
              {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="google-drive-pagination-button"
            >
              <ArrowRight02Icon size={16} />
            </button>
          </div>
          <button
            className="google-drive-modal-footer-button"
            onClick={processGoogleDriveFiles}
            disabled={isGoogleDriveFilesLoading || googleDriveFiles.filter(isGoogleDriveFileSelected).length === 0 || isGoogleDriveFileUploading}
          >
            {isGoogleDriveFileUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};